import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { formatDateIST, formatDateTimeIST } from "../utils/dateUtils";
import { format } from "date-fns";
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Download,
  Eye,
  Filter,
  FileImage,
  FileType,
  Loader2,
  CalendarIcon,
  QrCode,
  Shield,
  Image as ImageIcon,
  ClipboardList,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function TestReportsPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // test object to confirm delete
  const [deletingId, setDeletingId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (dateFilter && dateFilter !== "all") {
      params.append("date_filter", dateFilter);
    } else if (dateFrom || dateTo) {
      if (dateFrom) params.append("date_from", format(dateFrom, "yyyy-MM-dd'T'00:00:00"));
      if (dateTo) params.append("date_to", format(dateTo, "yyyy-MM-dd'T'23:59:59"));
    }
    if (statusFilter !== "all") params.append("status", statusFilter);
    return params;
  }, [dateFilter, dateFrom, dateTo, statusFilter]);

  const fetchPage = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = buildFilterParams();
      params.append("page", page);
      params.append("limit", PAGE_SIZE);

      const [testsRes, statsRes] = await Promise.all([
        axios.get(`${API}/tests?${params}`, { headers }),
        axios.get(`${API}/tests/stats?${buildFilterParams()}`, { headers }),
      ]);
      setTests(testsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load test reports");
    } finally {
      setLoading(false);
    }
  }, [headers, buildFilterParams]);

  // Re-fetch page 1 whenever filters (date, status) change
  const fetchTests = useCallback(async () => {
    setCurrentPage(1);
    await fetchPage(1);
  }, [fetchPage]);

  // Status filter change — re-fetch immediately
  const handleStatusFilterChange = useCallback((value) => {
    setStatusFilter(value);
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  useEffect(() => {
    axios.get(`${API}/products`, { headers })
      .then(res => setProducts(res.data))
      .catch(() => {});
  }, [headers]);

  const modelNumberByProductId = useMemo(() => {
    const map = {};
    for (const product of products) {
      map[product.id] = product.model_name;
    }
    return map;
  }, [products]);

  const handleDeleteTest = async () => {
    if (!deleteConfirm) return;
    setDeletingId(deleteConfirm.id);
    try {
      await axios.delete(`${API}/tests/${deleteConfirm.id}`, { headers });
      toast.success("Test deleted");
      setTests(prev => prev.filter(t => t.id !== deleteConfirm.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete test");
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  const handleDownloadPdf = async (testId, includeImages = true) => {
    setDownloadingId(`${testId}-${includeImages}`);
    try {
      const response = await axios.get(
        `${API}/tests/${testId}/report/pdf?include_images=${includeImages}`, 
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = includeImages ? `report_${testId}_with_images.pdf` : `report_${testId}_summary.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(includeImages ? "Full report downloaded!" : "Summary report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== "custom") {
      setDateFrom(null);
      setDateTo(null);
    }
  };

  // Client-side search within the loaded page
  const filteredTests = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return tests;
    return tests.filter(test =>
      test.serial_number?.toLowerCase().includes(q) ||
      test.product_name?.toLowerCase().includes(q) ||
      test.tester_name?.toLowerCase().includes(q) ||
      modelNumberByProductId[test.product_id]?.toLowerCase().includes(q)
    );
  }, [tests, searchTerm, modelNumberByProductId]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const sortedTests = useMemo(() => {
    if (!sortConfig.key) return filteredTests;
    const getValue = (test) => (sortConfig.key === "model_number"
      ? modelNumberByProductId[test.product_id]
      : test.serial_number || "") || "";
    const sorted = [...filteredTests].sort((a, b) =>
      getValue(a).localeCompare(getValue(b), undefined, { sensitivity: "base" })
    );
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [filteredTests, sortConfig, modelNumberByProductId]);

  // Server-side total for pagination: use stats count matching the active status filter
  const serverTotal = stats
    ? (statusFilter === "passed" ? stats.passed
      : statusFilter === "failed" ? stats.failed
      : statusFilter === "in_progress" ? stats.in_progress
      : stats.total)
    : 0;
  const totalPages = Math.max(1, Math.ceil(serverTotal / PAGE_SIZE));

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPage(page);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />;
    return sortConfig.direction === "asc"
      ? <ArrowUp className="w-3.5 h-3.5" />
      : <ArrowDown className="w-3.5 h-3.5" />;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "passed":
        return <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" />Passed</Badge>;
      case "failed":
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case "in_progress":
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />In Progress</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const totalTests = stats?.total ?? 0;
  const passedTests = stats?.passed ?? 0;
  const failedTests = stats?.failed ?? 0;
  const inProgressTests = stats?.in_progress ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="test-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Test Reports</h2>
          <p className="text-muted-foreground">
            {user?.role === "quality_engineer" ? "Your test history and reports" : "View and download test reports"}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tests</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTests}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Passed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">{passedTests}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{failedTests}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">{inProgressTests}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search serial, product, tester..."
                className="pl-9 w-[250px]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Filter */}
            <Select value={dateFilter} onValueChange={handleDateFilterChange}>
              <SelectTrigger className="w-[150px]" data-testid="date-filter">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="last_week">Last 7 Days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom Date Range */}
            {dateFilter === "custom" && (
              <>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[130px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "dd-MM-yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[130px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "dd-MM-yyyy") : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button variant="outline" size="sm" onClick={fetchTests}>
                  Apply
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>


      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Test Reports</CardTitle>
          <CardDescription>
            {serverTotal} report{serverTotal !== 1 ? "s" : ""} found
            {totalPages > 1 && ` · page ${currentPage} of ${totalPages}`}
            {searchTerm && filteredTests.length !== tests.length && ` · ${filteredTests.length} matching search on this page`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No test reports found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort("model_number")}
                        data-testid="sort-model-number"
                      >
                        Model Number {renderSortIcon("model_number")}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort("serial_number")}
                        data-testid="sort-serial-number"
                      >
                        Serial # {renderSortIcon("serial_number")}
                      </button>
                    </TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Tester</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTests.map((test) => (
                    <TableRow key={test.id} data-testid={`test-row-${test.id.slice(0,8)}`}>
                      <TableCell className="font-mono text-sm">
                        {modelNumberByProductId[test.product_id] || "—"}
                      </TableCell>
                      <TableCell className="font-mono font-medium">
                        {test.serial_number || <span className="text-muted-foreground font-normal">N/A</span>}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.product_name}</p>
                          {test.power_supply && (
                            <p className="text-xs text-muted-foreground">{test.power_supply}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{test.tester_name}</TableCell>
                      <TableCell>{getStatusBadge(test.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                test.status === "passed" ? "bg-emerald-500" :
                                test.status === "failed" ? "bg-red-500" : "bg-blue-500"
                              }`}
                              style={{ width: `${((test.step_results ? test.step_results.filter(sr => ["pass","fail","warning","skipped"].includes(sr.status)).length : test.current_step) / test.total_steps) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {test.step_results ? test.step_results.filter(sr => ["pass","fail","warning","skipped"].includes(sr.status)).length : test.current_step}/{test.total_steps}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{formatDateIST(test.started_at)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTimeIST(test.started_at).split(' ').slice(-2).join(' ')}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {test.status !== "in_progress" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPdf(test.id, false)}
                                disabled={downloadingId === `${test.id}-false`}
                                title="Summary Report (no images)"
                                data-testid={`download-summary-${test.id.slice(0,8)}`}
                              >
                                {downloadingId === `${test.id}-false` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FileType className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadPdf(test.id, true)}
                                disabled={downloadingId === `${test.id}-true`}
                                title="Full Report (with images)"
                                data-testid={`download-full-${test.id.slice(0,8)}`}
                              >
                                {downloadingId === `${test.id}-true` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FileImage className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          )}
                          {test.status !== "in_progress" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/${user?.role === "quality_engineer" ? "tester" : "admin"}/custom-qc-report/${test.id}`)}
                              title="Custom QC Report"
                              data-testid={`custom-qc-${test.id.slice(0,8)}`}
                            >
                              <ClipboardList className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tester/verify/${test.id}`)}
                            title="View Details"
                            data-testid={`view-test-${test.id.slice(0,8)}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {test.status === "in_progress" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(test)}
                              disabled={deletingId === test.id}
                              title="Delete Test"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              {deletingId === test.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-3 border-t">
                  <span className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, serverTotal)} of {serverTotal}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "…" ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground text-sm">…</span>
                        ) : (
                          <Button
                            key={p}
                            variant={currentPage === p ? "default" : "outline"}
                            size="sm"
                            className="w-8"
                            onClick={() => handlePageChange(p)}
                            disabled={loading}
                          >
                            {p}
                          </Button>
                        )
                      )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    <AlertDialog open={!!deleteConfirm} onOpenChange={open => !open && setDeleteConfirm(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Test?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the in-progress test{deleteConfirm?.serial_number ? <> for serial <strong>{deleteConfirm.serial_number}</strong></> : ""}. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDeleteTest} className="bg-red-600 hover:bg-red-700">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </div>
  );
}
